import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState(result === "success" ? "checking" : "cancelled");

  useEffect(() => {
    const sid = params.get("session_id");
    if (result !== "success" || !sid) return;
    let tries = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      tries += 1;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (["expired", "failed"].includes(r.data.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (tries < 15) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
    return () => {
      stop = true;
    };
  }, [result, params, refresh]);

  const content = {
    checking: [Loader2, "Confirming your payment…", "This usually takes a few seconds.", "text-slate-400 animate-spin"],
    paid: [CheckCircle2, "You're all set", "Your plan has been upgraded.", "text-emerald-600"],
    failed: [XCircle, "Payment not completed", "Your card was not charged. You can try again from Billing.", "text-rose-600"],
    timeout: [Loader2, "Still processing", "We'll update your plan as soon as Stripe confirms the payment.", "text-amber-600"],
    cancelled: [XCircle, "Checkout cancelled", "No payment was taken.", "text-slate-500"],
  }[state];
  const [Icon, title, text, cls] = content;
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="panel max-w-md w-full text-center py-12" data-testid="payment-result">
        <Icon className={`w-12 h-12 mx-auto mb-4 ${cls}`} />
        <h1 className="text-3xl font-extrabold" data-testid="payment-result-title">{title}</h1>
        <p className="text-slate-500 mt-2">{text}</p>
        <Link to="/app/billing" className="inline-block mt-6 px-5 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold" data-testid="payment-back-billing">
          Back to billing
        </Link>
      </div>
    </div>
  );
}
